import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import axios from "axios";
import { getAuthHeaders } from "../lib/authHeaders";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Users, User, Clock, FileText, TrendingUp, Plus, ArrowLeft, Search, Activity, BarChart3 } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';

const API = `${""}/api`;

const STATUS_COLORS = { active: "bg-green-100 text-green-800", follow_up: "bg-yellow-100 text-yellow-800", completed: "bg-gray-100 text-gray-700" };
const STATUS_LABELS = { active: "Active", follow_up: "Needs Follow-up", completed: "Completed" };
const SCORE_LABELS = ["Energy", "Sleep", "Pain", "Mood", "Weight Satisfaction"];

const PatientDashboard = ({ setCurrentView, assessment, setAssessment, setAssessmentId, generatedProtocol, currentUser, setSelectedPatientId }) => {
  const [view, setView] = useState("list"); // list | detail | visit
  const [patients, setPatients] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterTag, setFilterTag] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [loading, setLoading] = useState(false);
  const [visitNotes, setVisitNotes] = useState("");
  const [visitScores, setVisitScores] = useState({});
  const notesRef = useRef(null);

  const practitionerCode = (() => {
    // Use auth user ID first, then legacy beta code
    if (currentUser?.id) return currentUser.id;
    try { const d = JSON.parse(localStorage.getItem("pp_auth") || "{}"); return d.user_id || "DEMO"; } catch {}
    try { const d = JSON.parse(localStorage.getItem("beta_access") || "{}"); return d.code || "DEMO"; } catch { return "DEMO"; }
  })();

  const [labTrends, setLabTrends] = useState(null);

  const loadPatients = async () => {
    setLoading(true);
    try {
      const token = (() => { try { return JSON.parse(localStorage.getItem("pp_auth") || "{}").token; } catch { return null; } })();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/patients?practitioner_code=${practitionerCode}`, { headers });
      if (res.data.success) { setPatients(res.data.patients); setStats(res.data.stats); }
    } catch (e) { console.error("Load patients error:", e); }
    finally { setLoading(false); }
  };

  const loadPatientDetail = async (patientId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/patients/${patientId}`, { headers: getAuthHeaders() });
      if (res.data.success) {
        setSelectedPatient(res.data.patient);
        setView("detail");
        // Load lab trends
        try {
          const trendsRes = await axios.get(`${API}/patients/${patientId}/lab-trends`, { headers: getAuthHeaders() });
          if (trendsRes.data.success) setLabTrends(trendsRes.data.trends);
        } catch { setLabTrends(null); }
      }
    } catch (e) { console.error("Load patient error:", e); }
    finally { setLoading(false); }
  };

  const createPatientFromProtocol = async () => {
    if (!assessment || !generatedProtocol) { toast.info("Generate a protocol first, then create patient file."); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/patients`, {
        practitioner_code: practitionerCode,
        patient_data: assessment,
        assessment: assessment,
        protocol: generatedProtocol
      }, { headers: getAuthHeaders() });
      if (res.data.success) {
        toast.success(`Patient file created for ${assessment.patient_name || 'Patient'}!`);
        await loadPatients();
        loadPatientDetail(res.data.patient.id);
      }
    } catch (e) { toast.error("Error creating patient file"); }
    finally { setLoading(false); }
  };

  const updateStatus = async (patientId, status) => {
    await axios.put(`${API}/patients/${patientId}/status`, { status }, { headers: getAuthHeaders() });
    if (selectedPatient) loadPatientDetail(patientId);
    loadPatients();
  };

  const saveVisitNotes = async (visitId) => {
    const notes = notesRef.current?.value || "";
    await axios.put(`${API}/patients/${selectedPatient.id}/visits/${visitId}/notes`, { notes }, { headers: getAuthHeaders() });
    loadPatientDetail(selectedPatient.id);
  };

  const saveVisitScores = async (visitId, scores) => {
    await axios.put(`${API}/patients/${selectedPatient.id}/visits/${visitId}/scores`, { scores }, { headers: getAuthHeaders() });
    loadPatientDetail(selectedPatient.id);
  };

  useEffect(() => { loadPatients(); }, [practitionerCode]);

  const filtered = patients.filter(p => {
    const matchSearch = !searchTerm || p.name?.toLowerCase().includes(searchTerm.toLowerCase()) || p.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchTag = filterTag === "all" || (p.tags || []).includes(filterTag);
    const matchStatus = filterStatus === "all" || p.status === filterStatus;
    return matchSearch && matchTag && matchStatus;
  });

  const allTags = [...new Set(patients.flatMap(p => p.tags || []))];

  // ── PATIENT DETAIL VIEW ──
  if (view === "detail" && selectedPatient) {
    const visits = selectedPatient.visits || [];
    return (
      <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6 overflow-x-hidden" data-testid="patient-detail">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => { setView("list"); setSelectedPatient(null); }}><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
          <h1 className="text-2xl font-bold flex-1">{selectedPatient.name}</h1>
          <Badge className={STATUS_COLORS[selectedPatient.status] || ""}>{STATUS_LABELS[selectedPatient.status] || selectedPatient.status}</Badge>
        </div>

        {/* Patient Info Card */}
        <Card>
          <CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><span className="text-gray-500">Age:</span> <strong>{selectedPatient.age}</strong></div>
            <div><span className="text-gray-500">Gender:</span> <strong>{selectedPatient.gender}</strong></div>
            <div><span className="text-gray-500">Weight:</span> <strong>{selectedPatient.weight} lbs</strong></div>
            <div><span className="text-gray-500">Email:</span> <strong>{selectedPatient.email}</strong></div>
            <div className="col-span-2"><span className="text-gray-500">Concerns:</span> <strong>{(selectedPatient.primary_concerns || []).join(", ")}</strong></div>
            <div className="col-span-2 flex gap-1 flex-wrap">
              {(selectedPatient.tags || []).map((t, i) => <Badge key={i} variant="secondary" className="text-xs">{t}</Badge>)}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" onClick={async () => {
            if (setAssessment && selectedPatient) {
              // Pre-fill from patient record first
              const prefill = {
                patient_name: selectedPatient.patient_name || selectedPatient.name || '',
                age: selectedPatient.age || '',
                gender: selectedPatient.gender || '',
                weight: selectedPatient.weight || '',
                height_feet: selectedPatient.height_feet || '',
                height_inches: selectedPatient.height_inches || '',
                email: selectedPatient.email || '',
                primary_concerns: selectedPatient.primary_concerns || [],
                health_goals: selectedPatient.health_goals || [],
                current_medications: selectedPatient.current_medications || selectedPatient.medications || [],
                lifestyle_factors: selectedPatient.lifestyle_factors || {},
                medical_history: selectedPatient.medical_history || selectedPatient.conditions || [],
                allergies: selectedPatient.allergies || [],
              };
              // Try to fetch last assessment for this patient to get meds/allergies/labs
              try {
                const res = await axios.get(`${API}/patients/${selectedPatient.id}/last-assessment`, { headers: getAuthHeaders() });
                if (res.data?.success && res.data.assessment) {
                  const lastA = res.data.assessment;
                  if (lastA.current_medications?.length) prefill.current_medications = lastA.current_medications;
                  if (lastA.allergies?.length) prefill.allergies = lastA.allergies;
                  if (lastA.medical_history?.length) prefill.medical_history = lastA.medical_history;
                  if (lastA.health_goals?.length) prefill.health_goals = lastA.health_goals;
                  if (lastA.lifestyle_factors && Object.keys(lastA.lifestyle_factors).length) prefill.lifestyle_factors = lastA.lifestyle_factors;
                  if (lastA.manual_labs) prefill.manual_labs = lastA.manual_labs;
                }
              } catch {}
              setAssessment(prefill);
              if (setAssessmentId) setAssessmentId(null);
              toast.success(`Pre-filled data for ${prefill.patient_name || 'patient'} — review and update what changed`);
            }
            setCurrentView("assessment");
          }} data-testid="new-visit-btn"><Plus className="h-3 w-3 mr-1" /> New Visit</Button>
          <Button size="sm" variant="outline" onClick={() => { if (setSelectedPatientId) setSelectedPatientId(selectedPatient.id); setCurrentView("progress"); }}><TrendingUp className="h-3 w-3 mr-1" /> Progress Tracking</Button>
          <Button variant="outline" size="sm" onClick={() => updateStatus(selectedPatient.id, "active")}>Set Active</Button>
          <Button variant="outline" size="sm" onClick={() => updateStatus(selectedPatient.id, "follow_up")}>Needs Follow-up</Button>
          <Button variant="outline" size="sm" onClick={() => updateStatus(selectedPatient.id, "completed")}>Completed</Button>
        </div>

        {/* Progress Score Charts */}
        {visits.length >= 1 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4 text-teal-600" /> Progress Tracking</CardTitle></CardHeader>
            <CardContent className="p-4 space-y-6">
              {/* Progress Score Line Chart */}
              {(() => {
                const chartData = visits.slice().reverse().map((v, i) => ({
                  visit: `Visit ${i + 1}`,
                  date: new Date(v.visit_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                  energy: v.progress_scores?.energy || null,
                  sleep: v.progress_scores?.sleep || null,
                  pain: v.progress_scores?.pain || null,
                  mood: v.progress_scores?.mood || null,
                  weight_satisfaction: v.progress_scores?.weight_satisfaction || null,
                }));
                const hasScores = chartData.some(d => d.energy || d.sleep || d.pain || d.mood || d.weight_satisfaction);
                if (!hasScores) return (
                  <div className="text-center py-6 text-sm text-gray-400">
                    <BarChart3 className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p>No progress scores recorded yet.</p>
                    <p className="text-xs mt-1">Rate Energy, Sleep, Pain, Mood, and Weight below on each visit to see trends.</p>
                  </div>
                );
                return (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Progress Scores Over Time</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis domain={[0, 10]} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Line type="monotone" dataKey="energy" stroke="#0d9488" name="Energy" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                        <Line type="monotone" dataKey="sleep" stroke="#6366f1" name="Sleep" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                        <Line type="monotone" dataKey="mood" stroke="#f59e0b" name="Mood" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                        <Line type="monotone" dataKey="pain" stroke="#ef4444" name="Pain" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                        <Line type="monotone" dataKey="weight_satisfaction" stroke="#8b5cf6" name="Weight" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                );
              })()}

              {/* Weight Trend */}
              {(() => {
                const weightData = visits.slice().reverse()
                  .filter(v => v.assessment?.weight)
                  .map((v, i) => ({
                    date: new Date(v.visit_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                    weight: parseFloat(v.assessment.weight),
                  }));
                if (weightData.length < 2) return null;
                return (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Weight Trend (lbs)</h3>
                    <ResponsiveContainer width="100%" height={180}>
                      <AreaChart data={weightData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Area type="monotone" dataKey="weight" stroke="#0d9488" fill="#ccfbf1" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                );
              })()}

              {/* Summary Cards */}
              <div className="grid grid-cols-5 gap-3">
                {SCORE_LABELS.map(label => {
                  const key = label.toLowerCase().replace(/ /g, "_");
                  const scored = visits.filter(v => v.progress_scores?.[key]);
                  const first = scored[scored.length - 1]?.progress_scores?.[key];
                  const last = scored[0]?.progress_scores?.[key];
                  const diff = first && last ? last - first : null;
                  return (
                    <div key={key} className="text-center p-2 rounded-lg bg-gray-50">
                      <div className="text-[10px] text-gray-500 font-medium">{label}</div>
                      {first && last ? (
                        <>
                          <div className="text-lg font-bold text-gray-800">{last}<span className="text-xs text-gray-400">/10</span></div>
                          {diff !== 0 && <div className={`text-xs font-semibold ${diff > 0 ? 'text-green-600' : 'text-red-500'}`}>{diff > 0 ? '+' : ''}{diff} from baseline</div>}
                        </>
                      ) : <div className="text-sm text-gray-300">—</div>}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Visit History */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2"><Clock className="h-4 w-4" /> Visit History ({visits.length})</h2>
          {visits.length === 0 && <p className="text-gray-500 text-sm">No visits recorded yet.</p>}
          {visits.map((visit, idx) => (
            <Card key={visit.id} className={idx === 0 ? "border-teal-300 border-2" : ""}>
              <CardContent className="p-4 space-y-3">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-semibold">Visit {visits.length - idx} — {new Date(visit.visit_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>
                    {visit.protocol?.peptides?.length > 0 && (
                      <div className="flex gap-1 mt-1 flex-wrap">{visit.protocol.peptides.map((p, i) => <Badge key={i} variant="outline" className="text-xs">{p}</Badge>)}</div>
                    )}
                  </div>
                  {idx === 0 && <Badge className="bg-teal-100 text-blue-800">Latest</Badge>}
                </div>

                {/* Protocol Preview */}
                {visit.protocol?.clinical_reasoning_preview && (
                  <div className="text-sm text-gray-600 bg-gray-50 rounded p-3">{visit.protocol.clinical_reasoning_preview}...</div>
                )}

                {/* Treatment Tracker */}
                {visit.treatment_tracker?.length > 0 && (
                  <div className="text-sm">
                    <span className="font-medium text-gray-700">Current Treatments: </span>
                    {visit.treatment_tracker.map((t, i) => (
                      <Badge key={i} variant="secondary" className="mr-1 text-xs">{t.peptide}</Badge>
                    ))}
                  </div>
                )}

                {/* Progress Scores */}
                <div className="grid grid-cols-5 gap-2">
                  {SCORE_LABELS.map(label => {
                    const key = label.toLowerCase().replace(/ /g, "_");
                    const val = visit.progress_scores?.[key];
                    return (
                      <div key={key} className="text-center">
                        <div className="text-[10px] text-gray-500">{label}</div>
                        <select className="w-full text-xs border rounded p-1 text-center" value={val || ""}
                          onChange={(e) => {
                            const newScores = { ...visit.progress_scores, [key]: parseInt(e.target.value) || null };
                            saveVisitScores(visit.id, newScores);
                          }}>
                          <option value="">—</option>
                          {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </div>
                    );
                  })}
                </div>

                {/* Clinical Notes */}
                <div>
                  <div className="text-xs font-medium text-gray-500 mb-1">Clinical Notes</div>
                  <textarea ref={idx === 0 ? notesRef : null}
                    className="w-full text-sm border rounded p-2 min-h-[60px]"
                    defaultValue={visit.notes || ""}
                    placeholder="Add clinical observations, adjustments, patient feedback..."
                    onBlur={(e) => saveVisitNotes(visit.id)}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // ── PATIENT LIST VIEW (default) ──
  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6 overflow-x-hidden" data-testid="patient-dashboard">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl md:text-3xl font-bold">Patient Dashboard</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setCurrentView("assessment")} data-testid="add-patient-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Patient
          </Button>
          {generatedProtocol && assessment?.patient_name && (
            <Button onClick={createPatientFromProtocol} disabled={loading} size="sm" data-testid="create-patient-btn">
              <Plus className="h-4 w-4 mr-1" /> Create Patient File
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Total Patients", value: stats.total_patients || stats.total || 0, icon: Users, color: "text-teal-600" },
          { label: "Active", value: stats.active || 0, icon: Activity, color: "text-green-600" },
          { label: "Needs Follow-up", value: stats.needs_follow_up || 0, icon: Clock, color: "text-yellow-600" },
          { label: "Total Visits", value: stats.total_visits || 0, icon: FileText, color: "text-purple-600" },
        ].map((s, i) => (
          <Card key={i}>
            <CardContent className="p-4 text-center">
              <s.icon className={`h-5 w-5 mx-auto mb-1 ${s.color}`} />
              <div className="text-2xl font-bold">{s.value}</div>
              <div className="text-xs text-gray-500">{s.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search + Filters */}
      <div className="flex gap-3 flex-wrap items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <Input placeholder="Search patients..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9" data-testid="patient-search" />
        </div>
        <select className="border rounded-lg px-3 py-2 text-sm" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="follow_up">Needs Follow-up</option>
          <option value="completed">Completed</option>
        </select>
        <select className="border rounded-lg px-3 py-2 text-sm" value={filterTag} onChange={(e) => setFilterTag(e.target.value)}>
          <option value="all">All Tags</option>
          {allTags.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {/* Patient List */}
      {loading && <div className="text-center py-8 text-gray-500">Loading patients...</div>}
      {!loading && filtered.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p className="text-lg font-medium">No patients yet</p>
          <p className="text-sm mt-1">Generate a protocol from the Assessment wizard, then click "Create Patient File" to start tracking.</p>
        </div>
      )}
      <div className="space-y-2">
        {filtered.map(patient => (
          <Card key={patient.id} className="cursor-pointer hover:border-teal-300 transition-colors"
            onClick={() => loadPatientDetail(patient.id)} data-testid={`patient-card-${patient.id}`}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                <User className="h-5 w-5 text-teal-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{patient.name}</div>
                <div className="text-xs text-gray-500">
                  {patient.age ? `${patient.age}yo` : 'Age not provided'} {patient.gender} — {patient.visit_count || 0} visit{patient.visit_count !== 1 ? 's' : ''}
                  {patient.last_visit && ` — Last: ${new Date(patient.last_visit).toLocaleDateString()}`}
                </div>
              </div>
              <div className="flex gap-1 flex-wrap flex-shrink-0">
                {(patient.tags || []).slice(0, 3).map((t, i) => <Badge key={i} variant="secondary" className="text-[10px]">{t}</Badge>)}
              </div>
              <Badge className={`text-[10px] flex-shrink-0 ${STATUS_COLORS[patient.status] || ""}`}>{STATUS_LABELS[patient.status] || patient.status}</Badge>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default PatientDashboard;
